<template>
  <div>
    <div class="row">
      <template v-if="isPayType">
        <div class="col-sm-4 col-xs-12">
          <ValidationProvider>
            <div
              id="rate-type"
              class="form-group"
            >
              <label class="control-label">Pay type</label>
              <AppSelect
                v-model="rateType"
                :options="rateTypeOptions"
                :select2-options="defaultSelect2Options"
                :disabled="payPeriodIsNone || payPeriodIsExternal"
                disabled-tooltip="Select a pay period in order to set a pay type"
              />
            </div>
          </ValidationProvider>
        </div>
        <div class="pay-rate-wrapper col-sm-4 col-xs-12">
          <template v-if="!hasFixedRate">
            <ValidationProvider
              v-slot="{ errors }"
              name="payRate"
              mode="eager"
              :rules="`payRateRule|max_value:${RATE_LIMIT}`"
            >
              <div
                id="pay-rate"
                class="form-group"
                :class="{ 'has-error': errors[0] || formErrors('pay_rate') }"
              >
                <label class="control-label">Pay rate</label>
                <div class="input-group">
                  <AppInput
                    v-model="payRate"
                    type="number"
                  />
                  <span class="input-group-addon">{{ organizationCurrency }}/hr</span>
                </div>
                <span
                  v-if="errors[0]"
                  class="help-block"
                >{{ errors[0] }}</span>
                <span
                  v-if="formErrors('pay_rate')"
                  class="help-block"
                >{{ formErrors('pay_rate') }}</span>
              </div>
            </ValidationProvider>
          </template>
          <template v-else>
            <ValidationProvider
              v-slot="{ errors }"
              name="fixedAmount"
              mode="eager"
              :rules="`required|greaterThanZero|max_value:${RATE_LIMIT}`"
            >
              <div
                id="fixed-amount"
                class="form-group"
                :class="{ 'has-error': errors[0] }"
              >
                <label class="control-label">
                  <div class="d-flex align-items-center">
                    Fixed amount (per period)
                    <i
                      v-tooltip
                      class="hi hi-info-filled hi-12 a-tooltip mx-5"
                      title="This period is linked with the pay period. You can adjust it above if needed."
                    ></i>
                  </div>
                </label>
                <div
                  v-tooltip="payPeriodIsNone || payPeriodIsExternal"
                  class="input-group"
                  title="Select a pay period to set a fixed amount"
                >
                  <AppInput
                    v-model="fixedAmount"
                    type="number"
                    :disabled="payPeriodIsNone || payPeriodIsExternal"
                  />
                  <span class="input-group-addon">{{ organizationCurrency }}/{{ fixedAmountPeriodSuffix }}</span>
                </div>
                <span
                  v-if="errors[0]"
                  class="help-block"
                >{{ errors[0] }}</span>
              </div>
            </ValidationProvider>
          </template>
        </div>
        <ValidationProvider>
          <div class="col-sm-4 col-xs-12">
            <label class="control-label">
              <div class="d-flex align-items-center">
                Effective date
                <i
                  v-if="hasFixedRate"
                  v-tooltip
                  class="hi hi-info-filled hi-12 a-tooltip mx-5"
                  title="Fixed rate applies starting on the first day of current pay period. To apply this rate to prior periods, please contact your CS/CSM."
                ></i>
              </div>
            </label>
            <AppDatePicker
              v-model="payRateEffectiveDate"
              data-testid="member-pay-rate-effective-date"
              type="daily"
              :show-quick-pick="false"
              :max-date="maxRateEffectiveDate"
              :min-date="minRateEffectiveDate.payRate"
              :disabled="hasFixedRate"
              @input="onPayRateEffectiveDateInput"
            />
          </div>
        </ValidationProvider>
      </template>

      <template v-else>
        <div class="col-sm-6 col-xs-12">
          <ValidationProvider
            v-slot="{ errors }"
            name="billRate"
            mode="eager"
            :rules="`billRateRule|max_value:${RATE_LIMIT}`"
          >
            <div
              id="bill-rate"
              class="form-group"
              :class="{ 'has-error': errors[0] }"
            >
              <label class="control-label">Bill rate</label>
              <div class="input-group">
                <AppInput
                  v-model="billRate"
                  type="number"
                />
                <span class="input-group-addon">{{ organizationCurrency }}/hr</span>
              </div>
              <span class="help-block">{{ errors[0] }}</span>
            </div>
          </ValidationProvider>
        </div>
        <div class="col-sm-6 col-xs-12">
          <label class="control-label">Effective date</label>
          <AppDatePicker
            v-model="billRateEffectiveDate"
            data-testid="member-bill-rate-effective-date"
            type="daily"
            :show-quick-pick="false"
            :max-date="maxRateEffectiveDate"
            :min-date="minRateEffectiveDate.billRate"
            @input="onBillRateEffectiveDateInput"
          />
        </div>
      </template>
    </div>

    <AppConfirm
      v-if="showEffectiveDateConfirm"
      title="Override existing rates"
      confirm-label="Continue"
      @cancel="onEffectiveDateConfirmCancel"
      @confirm="onEffectiveDateConfirmConfirm"
    >
      {{ effectiveDateConfirmMessage }}
    </AppConfirm>

    <div class="row">
      <div
        v-if="!showNote"
        class="col-sm-12"
      >
        <a
          href="#"
          class="control-label-btn"
          style="display: flex; align-items: center; margin-top: 10px; text-decoration: none; cursor: pointer;"
          @click.prevent="showNote = true"
        >
          <i class="hi hi-blog hi-12" />
          <span class="ml-5">Add note</span>
        </a>
      </div>
    </div>

    <ValidationProvider
      ref="noteProvider"
      v-slot="{ errors }"
      rules="noteMax:100"
      mode="eager"
    >
      <div
        v-if="showNote"
        class="form-group"
        :class="{ 'has-error': errors[0] }"
      >
        <label
          :for="activeNoteId"
          class="control-label"
          :class="{ 'text-danger': errors[0] }"
        >Note</label>
        <div
          class="control-label-btn pull-right"
          @click="removeNote"
        >
          <a>Remove note</a>
        </div>
        <div :class="{ 'has-error': errors[0] }">
          <textarea
            :id="activeNoteId"
            v-model="activeNote"
            :placeholder="`Add a note to ${type} rate change`"
            rows="4"
            class="form-control text"
          />
          <div
            style="display: flex; align-items: center; min-height: 20px;"
            :class="{ 'help-block': errors[0] }"
          >
            <span v-if="errors[0]">{{ errors[0] }}</span>
            <span
              class="note-counter"
              :class="{ 'help-block': errors[0] }"
            >{{ activeNote?.length || 0 }}/100</span>
          </div>
        </div>
      </div>
    </ValidationProvider>

    <MembersRatesActivity
      :title="activeTabTitle"
      :type="type"
      wrapper-classes="mt-20"
    />
  </div>
</template>

<script>
import { extend } from 'vee-validate';
import { mapActions, mapGetters, mapState } from 'vuex';
import { RATE_LIMIT } from '~/app_constants';
import MembersRatesActivity from '~/components/members/MembersRatesActivity.vue';
import { mapFormFields } from '~/utils/form_utils';

extend('payRateRule', {
  validate: (value) => {
    if (parseFloat(value) >= 0.01) {return true;}

    return `Pay rate must be greater than or equal to 0.01. If you are trying to skip this member in Payroll set them to Manual instead.`;
  },
});

extend('billRateRule', {
  validate: (value) => {
    if (parseFloat(value) >= 0.01) {return true;}

    return 'Bill rate must be greater than or equal to 0.01.';
  },
});

extend('noteMax', {
  params: ['length'],
  validate(value, { length }) {
    return !value || value.length <= length;
  },
  message: `Note must be {length} characters or less`,
});

export default {
  name: 'MembersRatesFields',
  components: { MembersRatesActivity },
  props: {
    type: {
      type: String,
      required: true,
      validator: (v) => ['pay', 'bill'].includes(v),
    },
  },
  data: () => ({
    RATE_LIMIT,
    defaultSelect2Options: {
      allowClear: false,
      multiple: false,
      width: '100%',
    },
    payRateEffectiveDate: {},
    billRateEffectiveDate: {},
    showEffectiveDateConfirm: false,
    rateEffectiveDateConfirmType: 'pay_rate',
    showNote: false,
  }),
  computed: {
    ...mapState(['organization', 'errors']),
    ...mapGetters([
      'hasFixedRate',
      'payPeriodIsNone',
      'payPeriodIsExternal',
      'payPeriodOptions',
      'rateTypeOptions',
      'organizationTimeZone',
      'availableRateEffectiveDates',
      'defaultRateEffectiveDate',
    ]),
    ...mapFormFields('membership', {
      billRate: 'payments.bill_rate',
      fixedAmount: 'payments.fixed_amount',
      payPeriod: 'payments.pay_period',
      payRate: 'payments.pay_rate',
      rateType: 'payments.rate_type',
      payRateStartDate: 'payments.pay_rate_start_date',
      billRateStartDate: 'payments.bill_rate_start_date',
      payRateNote: 'payments.pay_rate_note',
      billRateNote: 'payments.bill_rate_note',
    }),
    isPayType() {
      return this.type === 'pay';
    },
    activeNote: {
      get() { return this.isPayType ? this.payRateNote : this.billRateNote; },
      set(v) {
        if (this.isPayType) {this.payRateNote = v;}
        else {this.billRateNote = v;}
      },
    },
    activeNoteId() {
      return this.isPayType ? 'pay-rate-note' : 'bill-rate-note';
    },
    activeTabTitle() {
      return this.isPayType ? 'Pay rate' : 'Bill rate';
    },
    activeRateKey() {
      return this.isPayType ? 'pay_rate' : 'bill_rate';
    },
    organizationCurrency() {
      return this.organization.currency.currency;
    },
    minRateEffectiveDate() {
      return {
        payRate: this.availableRateEffectiveDates.pay_rate[0] && moment(this.availableRateEffectiveDates.pay_rate[0]),
        billRate: this.availableRateEffectiveDates.bill_rate[0] && moment(this.availableRateEffectiveDates.bill_rate[0]),
      };
    },
    maxRateEffectiveDate() {
      return moment().tz(this.organizationTimeZone?.for);
    },
    fixedAmountPeriodSuffix() {
      if (this.payPeriodIsNone || this.payPeriodIsExternal) {return 'hr';}
      const option = this.payPeriodOptions.find((o) => o.id === this.payPeriod);

      return option?.text?.toLowerCase() || '';
    },
    effectiveDateConfirmMessage() {
      const date = moment(
        this.rateEffectiveDateConfirmType === 'pay_rate' ? this.payRateEffectiveDate : this.billRateEffectiveDate,
      ).format('MMM D, YYYY');

      return `The effective date of this rate change is set prior to existing rate changes and will override anything set after ${date}. You will need to save the page to confirm these changes.`;
    },
  },
  created() {
    this.setDefaultEffectiveDates();
  },
  methods: {
    ...mapActions(['updateField']),
    setDefaultEffectiveDates() {
      if (this.isPayType) {
        this.payRateEffectiveDate = moment().tz(this.organizationTimeZone?.for);
      } else {
        this.billRateEffectiveDate = moment().tz(this.organizationTimeZone?.for);
      }
    },
    onPayRateEffectiveDateInput(date) {
      const dateWithTZOffset = moment.tz(date, this.organizationTimeZone?.for);

      this.payRateStartDate = date.format();
      this.compareEffectiveDates(dateWithTZOffset, 'pay_rate');
    },
    onBillRateEffectiveDateInput(date) {
      const dateWithTZOffset = moment.tz(date, this.organizationTimeZone?.for);

      this.billRateStartDate = date.format();
      this.compareEffectiveDates(dateWithTZOffset, 'bill_rate');
    },
    compareEffectiveDates(date, key) {
      if (date.isBefore(this.defaultRateEffectiveDate[key]) || date.isSame(this.defaultRateEffectiveDate[key])) {
        this.rateEffectiveDateConfirmType = key;
        this.showEffectiveDateConfirm = true;
      }
    },
    onEffectiveDateConfirmCancel() {
      this.setDefaultEffectiveDates();
      this.resetRates();
      this.showEffectiveDateConfirm = false;
    },
    onEffectiveDateConfirmConfirm() {
      this.showEffectiveDateConfirm = false;
    },
    resetRates() {
      this.$emit('reset-rates');
    },
    removeNote() {
      this.activeNote = '';
      this.updateField(`payments.${this.activeRateKey}_note`, '');
      this.showNote = false;
      this.$refs.noteProvider?.reset();
    },
    formErrors(field) {
      return this.errors && this.errors[field];
    },
  },
};
</script>
